import React, { useState, useEffect } from 'react';
import { ChevronDown, Plus, Menu, X } from 'lucide-react';
import './Schedule.css';
import logo from '../components/assets/logo.png';
import { useNavigate } from "react-router-dom";

const Schedule = () => {
  const navigate = useNavigate();
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [selectedMonth, setSelectedMonth] = useState('February');

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile && !sidebarVisible) {
        // Reset sidebar visibility on desktop
        setSidebarVisible(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [sidebarVisible]);

  const toggleSidebar = () => {
    setSidebarVisible(!sidebarVisible);
  };

  const closeSidebar = () => {
    if (isMobile) {
      setSidebarVisible(false);
    }
  };
  
  const timeSlots = Array.from({ length: 10 }, (_, i) => {
    const hour = i + 9;
    return `${hour.toString().padStart(2, '0')}:00`;
  });

  const weekDays = [
    { day: 14, name: 'Sun' },
    { day: 15, name: 'Mon' },
    { day: 16, name: 'Tue' },
    { day: 17, name: 'Wed' },
    { day: 18, name: 'Thu' },
    { day: 19, name: 'Fri' },
    { day: 20, name: 'Sat' }
  ];

  const events = [
    { 
      id: 1, 
      title: '📸 Album Delivery', 
      startTime: '09:00', 
      duration: 2, 
      day: 14, 
      description: '30 shots, JPG format' 
    },
    { 
      id: 2, 
      title: '🎬 Video Editing', 
      startTime: '11:00', 
      duration: 2, 
      day: 16, 
      description: '45 min, MP4 format' 
    },
    { 
      id: 3, 
      title: '📷 Photo Session', 
      startTime: '13:00', 
      duration: 2, 
      day: 17, 
      description: '60 min, RAW format' 
    },
    { 
      id: 4, 
      title: '🎥 Client Meeting', 
      startTime: '15:00', 
      duration: 1, 
      day: 18, 
      description: 'Project discussion' 
    },
    { 
      id: 5, 
      title: '✨ Post Processing', 
      startTime: '10:00', 
      duration: 3, 
      day: 19, 
      description: 'Color grading & retouching' 
    }
  ];

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  return (
    <div className="schedule-container">
      {/* Toggle Button */}
      <button 
        className={`schedule-toggle-btn ${!sidebarVisible ? 'active' : ''}`}
        onClick={toggleSidebar}
      >
        <div className="schedule-toggle-icon">
          {sidebarVisible ? <Menu size={20} /> : <X size={20} />}
        </div>
      </button>

      {/* Overlay for mobile */}
      {isMobile && sidebarVisible && (
        <div className="schedule-overlay active" onClick={closeSidebar}></div>
      )}

      {/* Sidebar */}
      <aside className={`schedule-sidebar ${
        isMobile 
          ? (sidebarVisible ? 'show' : '') 
          : (sidebarVisible ? '' : 'hidden')
      }`}>
        <img src={logo} alt="LitStudio" className="schedule-logo" />
        <nav className="schedule-nav">
          <ul className="schedule-nav-list">
            {/* <li className="schedule-nav-item" onClick={() => {
              navigate("/Dashboard");
              closeSidebar();
            }}>
              📊 Dashboard
            </li> */}
            <li className="schedule-nav-item" onClick={() => {
              navigate("/Deals");
              closeSidebar();
            }}>
              💼 Deals
            </li>
            <li className="schedule-nav-item" onClick={() => {
              navigate("/Upload");
              closeSidebar();
            }}>
              📤 Upload
            </li>
            <li className="schedule-nav-item schedule-active" onClick={() => {
              navigate("/Schedule");
              closeSidebar();
            }}>
              📅 Schedule
            </li>
            <li className="schedule-nav-item" onClick={() => {
              navigate("/MemberShip");
              closeSidebar();
            }}>
              👑 Membership Plan
            </li>
          </ul>
        </nav>
        <div className="schedule-user-section">
          <div className="schedule-user-info">
            <div className="schedule-avatar">🚀</div>
            <div className="schedule-welcome-text">
              <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '2px' }}>Bula Studios</div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`schedule-main-content ${
        !sidebarVisible && !isMobile ? 'expanded' : ''
      }`}>
        <div className="schedule-header">
          <h1 className="schedule-title">Schedule Management</h1>
          <div className="schedule-header-actions">
            <div className="schedule-month-selector" onClick={() => {
              // You can add month selection logic here
              console.log('Month selector clicked');
            }}>
              <span className="schedule-month-text">📅 {selectedMonth}</span>
              <ChevronDown size={18} className="schedule-month-icon" style={{ color: '#ff4d4d' }} />
            </div>
            <button className="schedule-add-new-btn" onClick={() => {
              // You can add new event logic here
              console.log('Add new event clicked');
            }}>
              ➕ Add New <Plus size={18} className="schedule-add-icon" />
            </button>
          </div>
        </div>

        <div className="schedule-date-range">
          <h2 className="schedule-date-range-text">📆 February 14-20, 2024</h2>
        </div>

        <div className="schedule-grid">
          <div className="schedule-time-column">
            {timeSlots.map(time => (
              <div key={time} className="schedule-time-slot">
                🕐 {time}
              </div>
            ))}
          </div>

          <div className="schedule-days-grid">
            <div className="schedule-days-header">
              {weekDays.map(({ day, name }) => (
                <div key={day} className="schedule-day-column-header">
                  <div className="schedule-day-name">{name}</div>
                  <div className="schedule-day-number">{day}</div>
                </div>
              ))}
            </div>

            <div className="schedule-events-grid">
              {events.map(event => (
                <div
                  key={event.id}
                  className="schedule-event-card"
                  style={{
                    gridColumn: weekDays.findIndex(d => d.day === event.day) + 1,
                    gridRow: `span ${event.duration}`,
                    gridRowStart: (parseInt(event.startTime.split(':')[0]) - 8) * 2
                  }}
                  onClick={() => {
                    console.log('Event clicked:', event.title);
                    // You can add event details modal logic here
                  }}
                >
                  <div className="schedule-event-title">{event.title}</div>
                  <div className="schedule-event-description">
                    🕒 {event.startTime} • {event.description}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Additional Stats Section */}
        <div style={{ 
          marginTop: '2rem', 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '1rem' 
        }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(10px)',
            padding: '1.25rem',
            borderRadius: '16px',
            border: '1px solid rgba(230, 230, 230, 0.6)',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#ff4d4d', marginBottom: '0.5rem' }}>
              📅 {events.length}
            </div>
            <div style={{ fontSize: '0.875rem', color: '#666' }}>Total Events</div>
          </div>
          
          <div style={{
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(10px)',
            padding: '1.25rem',
            borderRadius: '16px',
            border: '1px solid rgba(230, 230, 230, 0.6)',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#ff4d4d', marginBottom: '0.5rem' }}>
              ⏰ {events.reduce((total, event) => total + event.duration, 0)}h
            </div>
            <div style={{ fontSize: '0.875rem', color: '#666' }}>Total Hours</div>
          </div>
          
          <div style={{
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(10px)',
            padding: '1.25rem',
            borderRadius: '16px',
            border: '1px solid rgba(230, 230, 230, 0.6)',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#ff4d4d', marginBottom: '0.5rem' }}>
              🎯 3
            </div>
            <div style={{ fontSize: '0.875rem', color: '#666' }}>Active Projects</div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Schedule;
